import { Chart, registerables, type ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';

// Servicios
import { type KPIData, fetchKPIData } from '../service/kpis.service';
// import { loadLoader } from '../utils/loader';

// Configuración de carga de componenetes de graficas
Chart.register(...registerables);

const commonOptions: ChartOptions<'line' | 'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: '#ffffff',
        font: {
          size: 12,
          weight: 'bold'
        }
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: '#4a5568',
      borderWidth: 1,
      cornerRadius: 8
    }
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit: 'month',
        displayFormats: { month: 'MMM yyyy' }
      },
      ticks: {
        autoSkip: true,
        maxTicksLimit: 12,
        color: '#e2e8f0',
        font: {
          size: 11,
          weight: 'bold'
        }
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)'
      }
    },
    y: {
      beginAtZero: true,
      ticks: {
        color: '#e2e8f0',
        font: {
          size: 11,
          weight: 'bold'
        }
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)'
      }
    }
  }
};

function toLocalMidnightUTC(dateString: string): Date {
  const d = new Date(dateString);
  // sumamos el offset para que 00:00 UTC → 00:00 hora local
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
  return d;
}

// Array global para poder actualizar todos los gráficos después
const charts: Chart<'line' | 'bar'>[] = [];

/**
 *
 */
export async function main() {
  // loadLoader({});
  document.addEventListener('DOMContentLoaded', async() => {
    const kpiData = await fetchKPIData();
    if (kpiData) {
      const sortedKpiData = sortKPIDataByMonth(kpiData); // Ordenar los datos por mes
      createTotalAccountsChart(sortedKpiData);
      createDocumentsSignedChart(sortedKpiData);
      createSignatoriesByTypeChart(sortedKpiData);
      createDocumentStatusChart(sortedKpiData);
      createDocumentsUploadedChart(sortedKpiData);
      createUsersWithoutUploadsChart(sortedKpiData);
      createUserBuyersChart(sortedKpiData);
      createUsersByPlanChart(sortedKpiData);
      createUsersUploadButNoSignChart(sortedKpiData);
      createAvailableDocumentsForSaleChart(sortedKpiData);
      createDocumentsAssignedChart(sortedKpiData);
      createSignaturesByTypeChart(sortedKpiData);
      createAssignedButNotUsedDocumentsChart(sortedKpiData);
      createUsedDocumentsChart(sortedKpiData);
      createSignatoriesWithOrWithoutGuestsChart(sortedKpiData);
      createSaldoFoliosUsuariosChart(sortedKpiData);
      createRecurrentUsersChart(sortedKpiData);
      createBlockchainDocumentsChart(sortedKpiData);
      createPurchasesByPlanChart(sortedKpiData);
      createSalesMxnChart(sortedKpiData);
    }
  });
}

// Función para formatear correctamente los meses y años
// function formatMonth(dateString: string): string {
//   const date = new Date(dateString);
//   const monthNames = [
//     'Jan',
//     'Feb',
//     'Mar',
//     'Apr',
//     'May',
//     'Jun',
//     'Jul',
//     'Aug',
//     'Sep',
//     'Oct',
//     'Nov',
//     'Dec'
//   ];
//   return `${monthNames[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
// }

// Función para ordenar los datos KPI por mes
function sortKPIDataByMonth(kpiData: KPIData[]): KPIData[] {
  return kpiData.sort(
    (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
  );
}

function createTotalAccountsChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'totalAccountsChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Cuentas Totales',
          data: kpiData.map((data) => data.total_accounts),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4
        },
        {
          label: 'Nuevas Cuentas Creadas',
          data: kpiData.map((data) => data.new_accounts_created),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createDocumentsSignedChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'documentsSignedChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos Firmados',
          data: kpiData.map((data) => data.document_signed),
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1
        },
        {
          label: 'Promedio de Documentos Firmados',
          data: kpiData.map((data) => data.average_documents_signed),
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createSignatoriesByTypeChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'signatoriesByTypeChart'
  ) as HTMLCanvasElement;
  const datasets = kpiData.reduce(
    (acc, data) => {
      data.signatories_without_account_by_type.forEach((signatory) => {
        if (!acc[signatory.typeOfSignOwner]) {
          acc[signatory.typeOfSignOwner] = [];
        }
        acc[signatory.typeOfSignOwner].push(signatory.numberOfUsers);
      });
      return acc;
    },
    {} as Record<string, number[]>
  );

  const chart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: Object.keys(datasets).map((key, index) => ({
        label: key,
        data: datasets[key],
        backgroundColor: `rgba(${index * 50}, 99, 132, 0.2)`,
        borderColor: `rgba(${index * 50}, 99, 132, 1)`,
        borderWidth: 1
      }))
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createDocumentStatusChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'documentStatusChart'
  ) as HTMLCanvasElement;
  const statuses = ['completed', 'send', 'init', 'cancelled'];

  const datasets = statuses.map((status) => {
    let backgroundColor = '';
    let borderColor = '';

    switch (status) {
      case 'completed':
        backgroundColor = 'rgba(75, 192, 192, 0.2)';
        borderColor = 'rgba(75, 192, 192, 1)';
        break;
      case 'send':
        backgroundColor = 'rgba(54, 162, 235, 0.2)';
        borderColor = 'rgba(54, 162, 235, 1)';
        break;
      case 'init':
        backgroundColor = 'rgba(255, 255, 255, 0.2)';
        borderColor = 'rgba(255, 255, 255, 1)';
        break;
      case 'cancelled':
        backgroundColor = 'rgba(255, 99, 132, 0.2)';
        borderColor = 'rgba(255, 99, 132, 1)';
        break;
    }

    return {
      label: status,
      data: kpiData.map((data) => {
        const statusData = data.document_status.find(
          (s) => s.status === status
        );
        return statusData ? parseInt(statusData.regular_count) : 0;
      }),
      backgroundColor,
      borderColor,
      borderWidth: 1
    };
  });

  const chart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createDocumentsUploadedChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'documentsUploadedChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos Subidos',
          data: kpiData.map((data) => data.total_uploaded_documents),
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createUsersWithoutUploadsChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'usersWithoutUploadsChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Cuentas sin Subir Documentos',
          data: kpiData.map((data) => data.accounts_no_upload_documents),
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createUserBuyersChart(kpiData: KPIData[]) {
  const ctx = document.getElementById('userBuyersChart') as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Usuarios Compradores',
          data: kpiData.map((data) => data.user_buyers),
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createUsersUploadButNoSignChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'usersUploadButNoSignChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Usuarios que Subieron pero no Firmaron',
          data: kpiData.map((data) => data.users_upload_but_no_sign),
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createAvailableDocumentsForSaleChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'availableDocumentsForSaleChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos Disponibles para la Venta',
          data: kpiData.map((data) => data.available_documents_for_sale),
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createDocumentsAssignedChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'documentsAssignedChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos Asignados',
          data: kpiData.map((data) => data.documents_assigned),
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createAssignedButNotUsedDocumentsChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'assignedButNotUsedDocumentsChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos Asignados pero no Usados',
          data: kpiData.map((data) => data.assigned_but_not_used_documents),
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

function createUsedDocumentsChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'usedDocumentsChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos Usados',
          data: kpiData.map((data) => data.used_documents),
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

// Nueva función para la gráfica de firmantes con y sin invitados
function createSignatoriesWithOrWithoutGuestsChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'signatoriesWithOrWithoutGuestsChart'
  ) as HTMLCanvasElement;
  const datasets = kpiData.reduce(
    (acc, data) => {
      data.signatories_with_guests_or_without_guest.forEach((signatory) => {
        if (!acc[signatory.Tipo_de_firma]) {
          acc[signatory.Tipo_de_firma] = [];
        }
        acc[signatory.Tipo_de_firma].push(signatory.Cuentas_que_han_firmado);
      });
      return acc;
    },
    {} as Record<string, number[]>
  );

  const chart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: Object.keys(datasets).map((key, index) => ({
        label: key,
        data: datasets[key],
        backgroundColor: `rgba(${index * 50}, 99, 132, 0.2)`,
        borderColor: `rgba(${index * 50}, 99, 132, 1)`,
        borderWidth: 1
      }))
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

// nueva función para la gráfica de usuarios recurrentes
function createRecurrentUsersChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'recurrentUsersChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Usuarios Recurrentes Históricos',
          data: kpiData.map((data) => data.historical_recurrent_users),
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        },
        {
          label: 'Usuarios Recurrentes del Mes',
          data: kpiData.map((data) => data.monthly_recurrent_users),
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

// Nueva función para la gráfica de documentos en blockchain
function createBlockchainDocumentsChart(kpiData: KPIData[]) {
  const ctx = document.getElementById(
    'blockchainDocumentsChart'
  ) as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Documentos en Blockchain',
          data: kpiData.map((data) => data.total_blockchain_documents),
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      ...commonOptions
    }
  });
  charts.push(chart);
  return chart;
}

// NUEVAS GRÁFICAS
function normalizeUsersByPlan(entry: KPIData['users_by_plan']): Record<string, number> {
  if (!entry) return {};
  if (Array.isArray(entry)) {
    return entry.reduce((acc, it: any) => {
      const key = it.plan ?? it.name ?? 'Desconocido';
      acc[key] = Number(it.users ?? it.total ?? 0);
      return acc;
    }, {} as Record<string, number>);
  }
  return entry as Record<string, number>;
}

function normalizeSignaturesByType(entry: KPIData['signatures_by_type']): Record<string, number> {
  if (!entry) return {};
  if (Array.isArray(entry)) {
    return entry.reduce((acc, it: any) => {
      const key = it.type ?? it.Tipo ?? 'Desconocido';
      acc[key] = Number(it.total ?? it.count ?? 0);
      return acc;
    }, {} as Record<string, number>);
  }
  return entry as Record<string, number>;
}

function createUsersByPlanChart(kpiData: KPIData[]) {
  const ctx = document.getElementById('usersByPlanChart') as HTMLCanvasElement;
  const labels = kpiData.map(d => toLocalMidnightUTC(d.month));

  // recolectar todas las claves (planes)
  const allKeys = new Set<string>();
  const normalized = kpiData.map(d => {
    const obj = normalizeUsersByPlan(d.users_by_plan);
    Object.keys(obj).forEach(k => allKeys.add(k));
    return obj;
  });

  const datasets = Array.from(allKeys).map((key, index) => ({
    label: key,
    data: normalized.map(obj => obj[key] ?? 0),
    backgroundColor: `rgba(${(index * 60) % 255}, 162, 235, 0.2)`,
    borderColor: `rgba(${(index * 60) % 255}, 162, 235, 1)`,
    borderWidth: 1
  }));

  const chart = new Chart(ctx, {
    type: 'bar',
    data: { labels, datasets },
    options: { ...commonOptions }
  });
  charts.push(chart);
  return chart;
}

function createSignaturesByTypeChart(kpiData: KPIData[]) {
  const ctx = document.getElementById('signaturesByTypeChart') as HTMLCanvasElement;
  const labels = kpiData.map(d => toLocalMidnightUTC(d.month));

  const allKeys = new Set<string>();
  const normalized = kpiData.map(d => {
    const obj = normalizeSignaturesByType(d.signatures_by_type);
    Object.keys(obj).forEach(k => allKeys.add(k));
    return obj;
  });

  const datasets = Array.from(allKeys).map((key, index) => ({
    label: key,
    data: normalized.map(obj => obj[key] ?? 0),
    backgroundColor: `rgba(153, ${(index * 50) % 255}, 255, 0.2)`,
    borderColor: `rgba(153, ${(index * 50) % 255}, 255, 1)`,
    borderWidth: 1
  }));

  const chart = new Chart(ctx, {
    type: 'bar',
    data: { labels, datasets },
    options: { ...commonOptions }
  });
  charts.push(chart);
  return chart;
}

function createSaldoFoliosUsuariosChart(kpiData: KPIData[]) {
  const ctx = document.getElementById('saldoFoliosUsuariosChart') as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Saldo de folios en usuarios',
          data: kpiData.map(d => Number(d.saldo_folios_usuarios ?? 0)),
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1
        }
      ]
    },
    options: { ...commonOptions }
  });
  charts.push(chart);
  return chart;
}

function normalizePurchasesByPlan(entry: KPIData['purchases_by_plan']): Record<string, number> {
  if (!entry) return {};
  if (Array.isArray(entry)) {
    return entry.reduce((acc, it: any) => {
      const key = it.plan ?? it.name ?? 'Desconocido';
      acc[key] = Number(it.purchases ?? it.count ?? it.total ?? 0);
      return acc;
    }, {} as Record<string, number>);
  }
  return entry as Record<string, number>;
}

function createPurchasesByPlanChart(kpiData: KPIData[]) {
  const ctx = document.getElementById('purchasesByPlanChart') as HTMLCanvasElement;
  const labels = kpiData.map(d => toLocalMidnightUTC(d.month));

  // recolectar todas las claves (planes)
  const allKeys = new Set<string>();
  const normalized = kpiData.map(d => {
    const obj = normalizePurchasesByPlan(d.purchases_by_plan);
    Object.keys(obj).forEach(k => allKeys.add(k));
    return obj;
  });

  const datasets = Array.from(allKeys).map((key, index) => ({
    label: key,
    data: normalized.map(obj => obj[key] ?? 0),
    backgroundColor: `rgba(${(index * 80 + 100) % 255}, ${(index * 60 + 50) % 255}, 200, 0.2)`,
    borderColor: `rgba(${(index * 80 + 100) % 255}, ${(index * 60 + 50) % 255}, 200, 1)`,
    borderWidth: 1
  }));

  const chart = new Chart(ctx, {
    type: 'bar',
    data: { labels, datasets },
    options: { ...commonOptions }
  });
  charts.push(chart);
  return chart;
}

function createSalesMxnChart(kpiData: KPIData[]) {
  const ctx = document.getElementById('salesMxnChart') as HTMLCanvasElement;
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: kpiData.map(d => toLocalMidnightUTC(d.month)),
      datasets: [
        {
          label: 'Ventas en MXN',
          data: kpiData.map(d => Number(d.sales_mxn ?? 0)),
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }
      ]
    },
    options: {
      ...commonOptions,
      scales: {
        ...commonOptions.scales,
        y: {
          ...commonOptions.scales?.y,
          ticks: {
            ...commonOptions.scales?.y?.ticks,
            callback: function(value) {
              return new Intl.NumberFormat('es-MX', {
                style: 'currency',
                currency: 'MXN',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(Number(value));
            }
          }
        }
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            ...commonOptions.plugins?.tooltip?.callbacks,
            label: function(context) {
              return new Intl.NumberFormat('es-MX', {
                style: 'currency',
                currency: 'MXN',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(Number(context.parsed.y));
            }
          }
        }
      }
    }
  });
  charts.push(chart);
  return chart;
}

const startInput = document.getElementById('kpi-start') as HTMLInputElement;
const endInput   = document.getElementById('kpi-end')   as HTMLInputElement;
const applyBtn   = document.getElementById('kpi-apply') as HTMLButtonElement;
const resetBtn   = document.getElementById('kpi-reset') as HTMLButtonElement;

applyBtn.addEventListener('click', () => {
  if (!startInput.value || !endInput.value) return;

  // construye tus Date como antes
  const start = new Date(startInput.value + '-01');
  const [y, m] = endInput.value.split('-').map(Number);
  const end   = new Date(y, m, 0, 23, 59, 59);

  // convierte a timestamp (milisegundos desde Epoch)
  const startMs = start.getTime();
  const endMs   = end.getTime();

  charts.forEach((chart) => {
    chart.options.scales!.x!.min = startMs;
    chart.options.scales!.x!.max = endMs;
    chart.update();
  });
});


resetBtn.addEventListener('click', () => {
  charts.forEach((chart) => {
    delete chart.options.scales!.x!['min'];
    delete chart.options.scales!.x!['max'];
    chart.update();
  });
  startInput.value = '';
  endInput.value   = '';
});

